class VideoTestimonials {
  constructor(sectionElement) {
    this.section = sectionElement
    this.splide = null
    this.videos = []
    this.currentVideoIndex = 0
    this.progressIntervals = new Map()
    this.sectionId = sectionElement.dataset.sectionId
    this.isVideoPlaying = false
    this.currentPlayingVideo = null
    this.titleSlideOffset = 1
    this._pausedByVisibility = false
    this.isTransitioning = false

    this.init()
  }

  init() {
    if (typeof window.Splide === "undefined") {
      setTimeout(() => this.init(), 100)
      return
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.initializeSlider())
    } else {
      this.initializeSlider()
    }
  }

  initializeSlider() {
    const splideElement = this.section.querySelector(".video-testimonials-splide")
    if (!splideElement) {
      console.error("[v0] Splide element not found")
      return
    }

    const settings = window.videoTestimonialsSettings?.[this.sectionId] || {}
    const slidesPerView = settings.slidesPerView || 3
    const slidesPerViewTablet = settings.slidesPerViewTablet || 2
    const slidesPerViewMobile = settings.slidesPerViewMobile || 1
    const spaceBetween = settings.spaceBetween || 20

    this.splide = new window.Splide(splideElement, {
      perPage: slidesPerView,
      gap: spaceBetween,
      focus: "center",
      start: 1,
      drag: true,
      autoplay: false,
      breakpoints: {
        1024: {
          perPage: slidesPerViewTablet,
          gap: spaceBetween,
        },
        550: {
          perPage: slidesPerViewMobile,
          gap: spaceBetween,
        },
      },
    })

    this.splide.on("mounted", () => {
      this.setupVideos()
      this.setupEventListeners()
      this.updateSlideCounter()
      this.handleSlideChange()
      setTimeout(() => {
        this.playVideo(0)
      }, 500)
    })

    this.splide.on("move", () => {
      this.handleSlideChange()
      this.updateSlideCounter()
    })

    this.splide.mount()
  }

  setupVideos() {
    this.videos = Array.from(this.section.querySelectorAll(".testimonial-video"))

    this.videos.forEach((video, index) => {
      video.controls = false
      video.muted = true
      video.playsInline = true

      video.addEventListener("loadedmetadata", () => {
        console.log(`[v0] Video ${index} metadata loaded, duration: ${video.duration}`)
      })

      video.addEventListener("timeupdate", () => {
        this.updateVideoProgress(video, index)
        if (video.duration > 0 && video.currentTime >= video.duration - 0.1) {
          if (!video.hasTriggeredEnd) {
            video.hasTriggeredEnd = true
            this.onVideoEnded(index)
          }
        }
      })

      video.addEventListener("ended", () => {
        if (!video.hasTriggeredEnd) {
          video.hasTriggeredEnd = true
          this.onVideoEnded(index)
        }
      })

      video.addEventListener("error", (e) => {
        console.error(`[v0] Video ${index} error:`, e)
      })

      video.addEventListener("click", (e) => {
        e.stopPropagation()
        this.toggleVideoPlayback(index)
      })
    })
  }

  toggleVideoPlayback(index) {
    const video = this.videos[index]
    if (!video) return
    
    const activeIndex = this.splide.index - this.titleSlideOffset
    if (activeIndex !== index) {
      return
    }

    if (video.paused) {
      if (this.currentPlayingVideo && this.currentPlayingVideo !== video) {
        this.stopAllVideos()
      }
      video.play().then(() => {
        this.isVideoPlaying = true
        this.currentPlayingVideo = video
        this.startProgressTracking(video, index)
      }).catch(error => {
        console.error(`[v0] Error playing video ${index}:`, error)
      })
    } else {
      video.pause()
      this.isVideoPlaying = false
      this.currentPlayingVideo = null
      this.stopProgressTracking(index)
    }
  }

  setupEventListeners() {
    const prevBtn = this.section.querySelector(".prev-btn")
    const nextBtn = this.section.querySelector(".next-btn")

    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        if (this.splide) {
          this.splide.go("<")
        }
      })
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        if (this.splide) {
          this.splide.go(">")
        }
      })
    }

    const videoSlides = this.section.querySelectorAll(".video-slide")
    videoSlides.forEach((slide, index) => {
      slide.addEventListener("click", (e) => {
        if (e.target.closest('.testimonial-video')) {
          return
        }
        if (index === 0) {
          return
        }
        if (this.isTransitioning) {
          return
        }
        const currentActiveIndex = this.splide.index
        if (currentActiveIndex === index) {
          const videoIndex = index - this.titleSlideOffset
          this.toggleVideoPlayback(videoIndex)
        } else {
          this.onVideoSlideClick(index)
        }
      })
    })

    const watchBtns = this.section.querySelectorAll(".watch-full-btn")
    watchBtns.forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        const videoIndex = Number.parseInt(btn.dataset.videoIndex)
        const youtubeUrl = btn.dataset.youtubeUrl

        if (youtubeUrl && youtubeUrl.trim() !== "") {
          this.openYouTubeModal(youtubeUrl)
        } else {
          this.onWatchFullVideo(videoIndex)
        }
      })
    })

    this.setupYouTubeModal()
  }

  handleSlideChange() {
    if (!this.splide) {
      return
    }

    this.isTransitioning = true
    
    setTimeout(() => {
      this.isTransitioning = false
    }, 500)

    this.stopAllVideosAndReset()

    let activeIndex = this.splide.index || 0

    if (activeIndex < 0) activeIndex = 0
    if (activeIndex >= this.videos.length + this.titleSlideOffset)
      activeIndex = this.videos.length + this.titleSlideOffset - 1

    const slides = this.section.querySelectorAll(".video-slide")
    slides.forEach((slide, index) => {
      slide.classList.remove("active")
      if (index === activeIndex) {
        slide.classList.add("active")
      }
    })

    this.currentVideoIndex = activeIndex
    this.updateSlideCounter()

    if (activeIndex > 0) {
      const videoIndex = activeIndex - this.titleSlideOffset
      setTimeout(() => {
        this.playVideo(videoIndex)
      }, 300)
    }
  }

  onVideoSlideClick(index) {
    if (!this.splide) {
      return
    }

    this.stopAllVideosAndReset()
    this.splide.go(index)

    if (index > 0) {
      const videoIndex = index - this.titleSlideOffset
      setTimeout(() => {
        this.playVideo(videoIndex)
      }, 300)
    }
  }

  playVideo(index) {
    if (index < 0 || index >= this.videos.length) {
      return
    }

    const video = this.videos[index]
    if (!video) {
      return
    }

    video.hasTriggeredEnd = false
    video.currentTime = 0

    this.isVideoPlaying = true
    this.currentPlayingVideo = video

    this.resetVideoProgress(index)

    const playPromise = video.play()
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          this.startProgressTracking(video, index)
        })
        .catch((error) => {
          console.error(`[v0] Error playing video ${index}:`, error)
          this.isVideoPlaying = false
          this.currentPlayingVideo = null
        })
    }
  }

  stopAllVideosAndReset() {
    this.videos.forEach((video, index) => {
      if (video) {
        if (!video.paused) {
          video.pause()
        }
        video.currentTime = 0
        video.hasTriggeredEnd = false
      }
      this.stopProgressTracking(index)
      this.resetVideoProgress(index)
    })

    this.isVideoPlaying = false
    this.currentPlayingVideo = null
  }

  stopAllVideos() {
    this.videos.forEach((video, index) => {
      if (video && !video.paused) {
        video.pause()
      }
      this.stopProgressTracking(index)
    })
    this.isVideoPlaying = false
    this.currentPlayingVideo = null
  }

  updateVideoProgress(video, index) {
    if (!video || video.duration === 0) return

    const progress = (video.currentTime / video.duration) * 100
    const progressBar = this.section.querySelector(
      `.video-slide:nth-child(${index + this.titleSlideOffset + 1}) .progress-fill`,
    )

    if (progressBar) {
      progressBar.style.width = `${progress}%`
    }
  }

  startProgressTracking(video, index) {
    this.stopProgressTracking(index)

    const interval = setInterval(() => {
      if (video.paused || video.ended) {
        this.stopProgressTracking(index)
        return
      }
      this.updateVideoProgress(video, index)
    }, 100)

    this.progressIntervals.set(index, interval)
  }

  stopProgressTracking(index) {
    const interval = this.progressIntervals.get(index)
    if (interval) {
      clearInterval(interval)
      this.progressIntervals.delete(index)
    }
  }

  resetVideoProgress(index) {
    const progressBar = this.section.querySelector(
      `.video-slide:nth-child(${index + this.titleSlideOffset + 1}) .progress-fill`,
    )
    if (progressBar) {
      progressBar.style.width = "0%"
    }
  }

  onVideoEnded(index) {
    this.stopProgressTracking(index)
    this.isVideoPlaying = false
    this.currentPlayingVideo = null

    if (!this.splide) {
      return
    }

    if (index < this.videos.length - 1) {
      const nextVideoIndex = index + 1
      const nextSlideIndex = nextVideoIndex + this.titleSlideOffset
      
      this.splide.go(nextSlideIndex)
      
      setTimeout(() => {
        const currentSlide = this.splide.index
        if (currentSlide === nextSlideIndex) {
          this.playVideo(nextVideoIndex)
        }
      }, 300)
      
    } else if (index === this.videos.length - 1) {
      const firstVideoSlideIndex = this.titleSlideOffset
      
      this.splide.go(firstVideoSlideIndex)
      
      setTimeout(() => {
        const currentSlide = this.splide.index
        if (currentSlide === firstVideoSlideIndex) {
          this.playVideo(0)
        }
      }, 300)
    }
  }

  onWatchFullVideo(index) {
    if (this.splide) {
      this.splide.go(index)
    }
  }

  setupYouTubeModal() {
    const modal = document.getElementById("youtube-modal")
    const closeBtn = modal.querySelector(".modal-close")
    const backdrop = modal.querySelector(".modal-backdrop")

    if (closeBtn) {
      closeBtn.addEventListener("click", () => {
        this.closeYouTubeModal()
      })
    }

    if (backdrop) {
      backdrop.addEventListener("click", () => {
        this.closeYouTubeModal()
      })
    }

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && modal.style.display !== "none") {
        this.closeYouTubeModal()
      }
    })
  }

  openYouTubeModal(youtubeUrl) {
    const modal = document.getElementById("youtube-modal")
    const iframe = document.getElementById("youtube-iframe")

    if (!modal || !iframe) {
      console.error("[v0] YouTube modal elements not found")
      return
    }

    const embedUrl = this.convertToEmbedUrl(youtubeUrl)

    if (!embedUrl) {
      console.error("[v0] Invalid YouTube URL:", youtubeUrl)
      return
    }

    this.stopAllVideos()
    iframe.src = embedUrl
    modal.style.display = "flex"
    document.body.style.overflow = "hidden"

    requestAnimationFrame(() => {
      modal.classList.add("show")
    })
  }

  closeYouTubeModal() {
    const modal = document.getElementById("youtube-modal")
    const iframe = document.getElementById("youtube-iframe")

    if (!modal || !iframe) return

    modal.classList.remove("show")

    setTimeout(() => {
      iframe.src = ""
      modal.style.display = "none"
      document.body.style.overflow = ""
    }, 500)
  }

  convertToEmbedUrl(youtubeUrl) {
    if (!youtubeUrl || typeof youtubeUrl !== "string") return null

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
    ]

    let videoId = null
    for (const pattern of patterns) {
      const match = youtubeUrl.match(pattern)
      if (match && match[1]) {
        videoId = match[1]
        break
      }
    }

    if (!videoId) return null

    return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`
  }

  updateSlideCounter() {
    const currentSlideEl = this.section.querySelector(".current-slide")
    if (!currentSlideEl || !this.splide) return

    let activeIndex = this.splide.index

    if (typeof activeIndex === "undefined" || activeIndex === null) {
      activeIndex = this.currentVideoIndex || 0
    }

    if (activeIndex < 0) activeIndex = 0
    if (activeIndex >= this.videos.length + this.titleSlideOffset)
      activeIndex = this.videos.length + this.titleSlideOffset - 1

    this.currentVideoIndex = activeIndex
    const displayIndex = (activeIndex + 1).toString().padStart(2, "0")
    currentSlideEl.textContent = displayIndex
  }

  destroy() {
    this.stopAllVideos()
    this.progressIntervals.forEach((interval) => clearInterval(interval))
    this.progressIntervals.clear()
    this.closeYouTubeModal()

    if (this.splide) {
      this.splide.destroy()
      this.splide = null
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const videoSections = document.querySelectorAll(".video-testimonials-section")

  videoSections.forEach((section) => {
    try {
      const instance = new VideoTestimonials(section)
      section._videoTestimonialsInstance = instance
    } catch (error) {
      console.error("[v0] Error initializing video testimonials:", error)
    }
  })
})

document.addEventListener("visibilitychange", () => {
  if (document.hidden) {
    document.querySelectorAll(".video-testimonials-section").forEach((section) => {
      const instance = section._videoTestimonialsInstance
      if (instance && instance.currentPlayingVideo && !instance.currentPlayingVideo.paused) {
        instance._pausedByVisibility = true
        instance.currentPlayingVideo.pause()
      }
    })
  } else {
    document.querySelectorAll(".video-testimonials-section").forEach((section) => {
      const instance = section._videoTestimonialsInstance
      if (instance && instance._pausedByVisibility && instance.currentPlayingVideo) {
        instance._pausedByVisibility = false
        instance.currentPlayingVideo.play().catch((err) => {
          console.warn("[v0] Could not resume video after tab focus:", err)
        })
      }
    })
  }
})